import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import API_URL from "../../config/ApiUrl";
import { getCandidateDetail, getCandidates } from "./candidateSlice";
import { base64ToBlobUrl } from "../../config/Global";

export const getCandidatesForms = createAsyncThunk(
  "Candidates/getCandidatesForms",
  async ({ candidateId, payload }, { rejectWithValue }) => {
    let apiUrl = "";
    try {
      if (payload) {
        const filteredPayload = Object.fromEntries(
          Object.entries(payload).filter(
            ([, value]) => value !== null && value !== undefined && value !== ""
          )
        );
        const params = new URLSearchParams(filteredPayload);
        const queryString = params.toString();
        apiUrl = `${API_URL.CANDIDATE_APPLICATION.GET_CANDIDATE_FORMS}/${candidateId}?${queryString}`;
      } else {
        apiUrl = `${API_URL.CANDIDATE_APPLICATION.GET_CANDIDATE_FORMS}/${candidateId}`;
      }
      const { data } = await axios.get(apiUrl);
      return data.data;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  }
);

export const getCandidatesDocuments = createAsyncThunk(
  "Candidates/getCandidatesDocuments",
  async ({ candidateId, payload }, { rejectWithValue }) => {
    let apiUrl = "";
    try {
      if (payload) {
        const filteredPayload = Object.fromEntries(
          Object.entries(payload).filter(
            ([, value]) => value !== null && value !== undefined && value !== ""
          )
        );
        const params = new URLSearchParams(filteredPayload);
        const queryString = params.toString();
        apiUrl = `${API_URL.CANDIDATE_APPLICATION.GET_CANDIDATE_DOCUMENTS}/${candidateId}?${queryString}`;
      } else {
        apiUrl = `${API_URL.CANDIDATE_APPLICATION.GET_CANDIDATE_DOCUMENTS}/${candidateId}`;
      }
      const { data } = await axios.get(apiUrl);
      return data.data;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  }
);

export const getFormsAndDocuments = createAsyncThunk(
  "Candidates/getFormsAndDocuments",
  async (candidateId, { rejectWithValue }) => {
    try {
      const { data } = await axios.get(
        `${API_URL.CANDIDATE_APPLICATION.GET_FORMS_DOCUMENTS}/${candidateId}`
      );
      return data.data;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  }
);

export const updateFormComplianceNotes = createAsyncThunk(
  "Candidates/updateFormComplianceNotes",
  async ({ candidateId, formId, notes }, { rejectWithValue }) => {
    try {
      const { data } = await axios.put(
        `${API_URL.CANDIDATE_APPLICATION.UPDATE_FORM_COMPLIANCE_NOTES}/${candidateId}/${formId}`,
        { notes }
      );
      return { formId, notes, data: data.data };
    } catch (error) {
      return rejectWithValue(error.data);
    }
  }
);

export const updateDocumentComplianceNotes = createAsyncThunk(
  "Candidates/updateDocumentComplianceNotes",
  async ({ candidateId, docId, notes }, { rejectWithValue }) => {
    try {
      const { data } = await axios.put(
        `${API_URL.CANDIDATE_APPLICATION.UPDATE_DOCUMENT_COMPLIANCE_NOTES}/${candidateId}/${docId}`,
        { notes }
      );
      return { docId, notes, data: data.data };
    } catch (error) {
      return rejectWithValue(error.data);
    }
  }
);

export const markDocumentNA = createAsyncThunk(
  "Candidates/markDocumentNA",
  async ({ candidateId, docId }, { rejectWithValue }) => {
    try {
      const { data } = await axios.put(
        `${API_URL.CANDIDATE.SLUG}/${candidateId}/Doc/${docId}/MarkNA`
      );
      return data.data;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  }
);

export const markFormNA = createAsyncThunk(
  "Candidates/markFormNA",
  async ({ candidateId, formId }, { rejectWithValue }) => {
    try {
      const { data } = await axios.put(
        `${API_URL.CANDIDATE.SLUG}/${candidateId}/Form/${formId}/MarkNA`
      );
      return data.data;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  }
);

export const unmarkDocumentNA = createAsyncThunk(
  "Candidates/unmarkDocumentNA",
  async ({ candidateId, docId }, { rejectWithValue }) => {
    try {
      const { data } = await axios.put(
        `${API_URL.CANDIDATE.SLUG}/${candidateId}/Doc/${docId}/UnmarkNA`
      );
      return data.data;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  }
);

export const unmarkFormNA = createAsyncThunk(
  "Candidates/unmarkFormNA",
  async ({ candidateId, formId }, { rejectWithValue }) => {
    try {
      const { data } = await axios.put(
        `${API_URL.CANDIDATE.SLUG}/${candidateId}/Form/${formId}/UnmarkNA`
      );
      return data.data;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  }
);

export const sendDocToCandidate = createAsyncThunk(
  "Candidates/sendDocToCandidate",
  async ({ candidateId, docId }, { rejectWithValue }) => {
    try {
      const { data } = await axios.put(
        `${API_URL.CANDIDATE.SLUG}/${candidateId}/Doc/${docId}/SendToCandidate`
      );
      return data.data;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  }
);

export const sendFormToCandidate = createAsyncThunk(
  "Candidates/sendFormToCandidate",
  async ({ candidateId, formId }, { rejectWithValue }) => {
    try {
      const { data } = await axios.put(
        `${API_URL.CANDIDATE.SLUG}/${candidateId}/Form/${formId}/SendToCandidate`
      );
      return data.data;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  }
);

export const bulkSend = createAsyncThunk(
  "Candidates/bulkSend",
  async ({ candidateId, formIds, documentIds }, { rejectWithValue }) => {
    try {
      const { data } = await axios.post(
        `${API_URL.CANDIDATE.SLUG}/${candidateId}/BulkSend`,
        { formIds, documentIds }
      );
      return data.data;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  }
);

export const uploadDocByCO = createAsyncThunk(
  "Candidates/uploadDocByCO",
  async ({ candidateId, docId, file, notes }, { rejectWithValue }) => {
    try {
      const formData = new FormData();
      formData.append("file", file);
      if (notes) formData.append("notes", notes);
      const { data } = await axios.post(
        `${API_URL.CANDIDATE.SLUG}/${candidateId}/Doc/${docId}/UploadByCO`,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      return data.data;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  }
);

export const uploadFormByCO = createAsyncThunk(
  "Candidates/uploadFormByCO",
  async ({ candidateId, formId, file, notes }, { rejectWithValue }) => {
    try {
      const formData = new FormData();
      formData.append("file", file);
      if (notes) formData.append("notes", notes);
      const { data } = await axios.post(
        `${API_URL.CANDIDATE.SLUG}/${candidateId}/Form/${formId}/UploadByCO`,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      return data.data;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  }
);

export const mapToChildrenPreview = createAsyncThunk(
  "Candidates/mapToChildrenPreview",
  async ({ candidateId, parentDocId }, { rejectWithValue }) => {
    try {
      const { data } = await axios.get(
        `${API_URL.CANDIDATE.SLUG}/${candidateId}/Doc/${parentDocId}/MapToChildren/Preview`
      );
      return data.data;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  }
);

export const mapToChildrenApply = createAsyncThunk(
  "Candidates/mapToChildrenApply",
  async ({ candidateId, parentDocId, childDocIds }, { rejectWithValue }) => {
    try {
      const { data } = await axios.post(
        `${API_URL.CANDIDATE.SLUG}/${candidateId}/Doc/${parentDocId}/MapToChildren`,
        { childDocIds }
      );
      return data.data;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  }
);

export const mapFormToChildrenPreview = createAsyncThunk(
  "Candidates/mapFormToChildrenPreview",
  async ({ candidateId, parentFormId }, { rejectWithValue }) => {
    try {
      const { data } = await axios.get(
        `${API_URL.CANDIDATE.SLUG}/${candidateId}/Form/${parentFormId}/MapToChildren/Preview`
      );
      return data.data;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  }
);

export const mapFormToChildrenApply = createAsyncThunk(
  "Candidates/mapFormToChildrenApply",
  async ({ candidateId, parentFormId, childFormIds }, { rejectWithValue }) => {
    try {
      const { data } = await axios.post(
        `${API_URL.CANDIDATE.SLUG}/${candidateId}/Form/${parentFormId}/MapToChildren`,
        { childFormIds }
      );
      return data.data;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  }
);

export const sendRegistrationLink = createAsyncThunk(
  "Candidates/sendRegistrationLink",
  async (
    { candidateId, page, payload },
    { rejectWithValue, getState, dispatch }
  ) => {
    try {
      const { data } = await axios.post(
        `${API_URL.CANDIDATE_APPLICATION.SEND_REGISTRATION_LINK}/${candidateId}`,
        payload
      );
      let { active_payload } = getState().candidate;
      if (page === "list") {
        dispatch(getCandidates(active_payload));
      } else {
        dispatch(getCandidateDetail(candidateId));
      }
      return data.data;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  }
);

export const addCandidateFormsDocs = createAsyncThunk(
  "Candidates/addCandidateFormsDocs",
  async ({ candidateId, payload }, { rejectWithValue, getState, dispatch }) => {
    try {
      const { data } = await axios.post(
        `${API_URL.CANDIDATE_APPLICATION.ADD_CANDIDATE_FORM_DOCS}/${candidateId}`,
        payload
      );
      let { active_payload } = getState().candidate;
      dispatch(getCandidates(active_payload));
      return data.data;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  }
);

export const resendLink = createAsyncThunk(
  "Candidates/resendLink",
  async (candidateId, { rejectWithValue }) => {
    try {
      const { data } = await axios.get(
        `${API_URL.CANDIDATE_APPLICATION.RESEND_LINK}/${candidateId}`
      );
      // dispatch(getCandidates);
      return data.data;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  }
);

export const downloadForm = createAsyncThunk(
  "Form/downloadForm",
  async ({ formId, onlyViewPdf = false }, { rejectWithValue }) => {
    //when want only view then pass onlyViewPdf = false
    try {
      const data = await axios.get(
        `${API_URL.CANDIDATE_APPLICATION.DOWNLOAD_FORM}/${formId}`
      );

      if (onlyViewPdf) {
        const pdfBlob = base64ToBlobUrl(data.data.data.fileData.content);
        return { url: pdfBlob, candidateFileData: data.data.data };
      } else {
        const fileURL = base64ToBlobUrl(data.data.data.fileData.content);
        const fileLink = document.createElement("a");
        fileLink.href = fileURL;

        fileLink.setAttribute(
          "download",
          data?.data?.data?.fileData?.name || `${formId}.pdf`
        );

        // Append to the document and trigger download
        document.body.appendChild(fileLink);
        fileLink.click();

        // Cleanup
        fileLink.parentNode.removeChild(fileLink);
        return base64ToBlobUrl(data.data.data.fileData.content);
      }
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const downloadDocuments = createAsyncThunk(
  "Form/downloadDocuments",
  async ({ docData, onlyView = false }, { rejectWithValue }) => {
    //in docData received docId and id(child doc id) when want particular that document view or download then pass it
    //when want only view then pass onlyView = false
    try {
      let docDetailsParam = "";
      if (onlyView) {
        docDetailsParam = docData.id ? `?DocDetailsId=${docData.id}` : "";
      } else {
        docDetailsParam = docData?.documentDetails?.length < 2 ? `?DocDetailsId=${docData.documentDetails[0].id}` : "";
      }
      const data = await axios.get(
        `${API_URL.CANDIDATE_APPLICATION.DOWNLOAD_DOCUMENT}/${docData.docId}${docDetailsParam}`,
        { responseType: "blob" }
      );
      if (onlyView) {
        // const pdfBlob = URL.createObjectURL(
        //   new Blob([data.data], { type: "application/pdf" })
        // );
        const docBlob = {};
        docBlob.file = URL.createObjectURL(data.data);
        docBlob.mimeType = data.data.type;
        return docBlob;
      } else {
        // Create a Blob from the response data
        const blob = new Blob([data.data], {
          type: data.data.type || "application/octet-stream",
        });

        // DEBUG: log all response headers
        console.log("[DOWNLOAD DEBUG] All response headers:", data.headers);
        console.log(
          "[DOWNLOAD DEBUG] content-disposition:",
          data.headers?.["content-disposition"]
        );

        // Extract filename from Content-Disposition header set by the backend
        let downloadFileName = docData.docId;
        const contentDisposition =
          data.headers?.["content-disposition"] ||
          data.headers?.["Content-Disposition"];
        if (contentDisposition) {
          const utf8Match = contentDisposition.match(
            /filename\*=UTF-8''([^;]+)/i
          );
          const asciiMatch = contentDisposition.match(
            /filename="?([^";\n]+)"?/i
          );
          if (utf8Match) {
            downloadFileName = decodeURIComponent(utf8Match[1]);
          } else if (asciiMatch) {
            downloadFileName = asciiMatch[1];
          }
        }
        console.log("[DOWNLOAD DEBUG] Final filename:", downloadFileName);

        // Create a URL for the Blob and set it as the download link
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = downloadFileName;

        // Trigger the download by clicking the link programmatically
        document.body.appendChild(link);
        link.click();

        // Clean up the object URL after the download
        URL.revokeObjectURL(url);
        document.body.removeChild(link);

        return data;
      }
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const getFormHistory = createAsyncThunk(
  "Candidates/getFormHistory",
  async ({ formId, candidateId }, { rejectWithValue }) => {
    try {
      const { data } = await axios.get(
        `${API_URL.CANDIDATE_APPLICATION.FORM_HISTORY}/${formId}/${candidateId}`
      );
      return data.data;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  }
);

export const getDocumentHistory = createAsyncThunk(
  "Candidates/getFormHistory",
  async ({ documentId, candidateId }, { rejectWithValue }) => {
    try {
      const { data } = await axios.get(
        `${API_URL.CANDIDATE_APPLICATION.DOCUMENT_HISTORY}/${documentId}/${candidateId}`
      );
      return data.data;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  }
);

export const deleteForm = createAsyncThunk(
  "Candidates/deleteForm",
  async (formId, { rejectWithValue, getState, dispatch }) => {
    try {
      const { data } = await axios.delete(
        `${API_URL.CANDIDATE_APPLICATION.DELETE_FORM}/${formId}`
      );
      let { candidate_details } = getState().candidate;
      dispatch(getFormsAndDocuments(candidate_details?.id));
      return data.data;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  }
);

export const deleteDocuments = createAsyncThunk(
  "Candidates/deleteDocuments",
  async (docId, { rejectWithValue, getState, dispatch }) => {
    try {
      const { data } = await axios.delete(
        `${API_URL.CANDIDATE_APPLICATION.DELETE_DOCUMENT}/${docId}`
      );
      let { candidate_details } = getState().candidate;
      dispatch(getFormsAndDocuments(candidate_details?.id));
      return data.data;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  }
);

export const getCandidateMessages = createAsyncThunk(
  "Candidates/getCandidateMessages",
  async (candidateId, { rejectWithValue }) => {
    try {
      const { data } = await axios.get(
        `${API_URL.CANDIDATE_APPLICATION.SLUG}/${candidateId}/Messages`
      );
      return data.data;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  }
);

export const getMessageDetail = createAsyncThunk(
  "Candidates/getMessageDetail",
  async (messageId, { rejectWithValue }) => {
    try {
      const { data } = await axios.get(
        `${API_URL.CANDIDATE.GET_MESSAGE_DETAIL_BY_ID}/${messageId}`
      );
      return data.data;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  }
);

export const rejectCandidateForm = createAsyncThunk(
  "rejectCandidateForm",
  async ({ payload, candidateId }, { rejectWithValue }) => {
    try {
      const { data } = await axios.post(
        `${API_URL.CANDIDATE_APPLICATION.SLUG}/${candidateId}/Form/Reject`,
        payload
      );
      return data.data;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  }
);

export const approveCandidateForm = createAsyncThunk(
  "rejectCandidateForm",
  async ({ payload, candidateId }, { rejectWithValue }) => {
    try {
      const { data } = await axios.put(
        `${API_URL.CANDIDATE_APPLICATION.SLUG}/${candidateId}/form/approval`,
        payload
      );
      return data.data;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  }
);

export const rejectCandidateDocument = createAsyncThunk(
  "rejectCandidateDocument",
  async ({ payload, candidateId }, { rejectWithValue }) => {
    try {
      const { data } = await axios.post(
        `${API_URL.CANDIDATE_APPLICATION.SLUG}/${candidateId}/Document/Reject`,
        payload
      );
      return data.data;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  }
);

export const approveCandidateDocument = createAsyncThunk(
  "rejectCandidateForm",
  async ({ payload, candidateId }, { rejectWithValue }) => {
    try {
      const { data } = await axios.put(
        `${API_URL.CANDIDATE_APPLICATION.SLUG}/${candidateId}/Doc/approval`,
        payload
      );
      return data.data;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  }
);

export const updateFormExpiry = createAsyncThunk(
  "candidateApplication/updateFormExpiry",
  async ({ payload, candidateId }, { rejectWithValue }) => {
    try {
      const { data } = await axios.post(
        `${API_URL.CANDIDATE_APPLICATION.SLUG}/${candidateId}/Form/UpdateExpiry`,
        payload
      );
      return data.data;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  }
);

export const updateDocExpiry = createAsyncThunk(
  "candidateApplication/updateDocExpiry",
  async ({ payload, candidateId }, { rejectWithValue }) => {
    try {
      const { data } = await axios.post(
        `${API_URL.CANDIDATE_APPLICATION.SLUG}/${candidateId}/Doc/UpdateExpiry`,
        payload
      );
      return data.data;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  }
);

export const getRejectedReason = createAsyncThunk(
  "Form/getRejectedReason",
  async (id, { rejectWithValue }) => {
    try {
      const { data } = await axios.get(
        `${API_URL.CANDIDATE_APPLICATION.REJECTED_REASON}/${id}`
      );
      return data.data;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const resendCandidateForm = createAsyncThunk(
  "Form/resendCandidateForm",
  async ({ payload, candidateId }, { rejectWithValue, getState, dispatch }) => {
    try {
      const { data } = await axios.post(
        `${API_URL.CANDIDATE_APPLICATION.SLUG}/${candidateId}/Form/Resend`,
        payload
      );
      let { candidate_details } = getState().candidate;
      dispatch(getFormsAndDocuments(candidate_details?.id));
      dispatch(getCandidateDetail(candidate_details?.id));
      return data.data;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const resendCandidateDocument = createAsyncThunk(
  "Form/resendCandidateDocument",
  async ({ payload, candidateId }, { rejectWithValue, getState, dispatch }) => {
    try {
      const { data } = await axios.post(
        `${API_URL.CANDIDATE_APPLICATION.SLUG}/${candidateId}/Document/Resend`,
        payload
      );
      let { candidate_details } = getState().candidate;
      dispatch(getFormsAndDocuments(candidate_details?.id));
      dispatch(getCandidateDetail(candidate_details?.id));
      return data.data;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const getNotificationLogs = createAsyncThunk(
  "ExpiryNotification/getLogs",
  async ({ candidateApplicationFormId, candidateApplicationDocId }, { rejectWithValue }) => {
    try {
      const params = new URLSearchParams();
      if (candidateApplicationFormId) params.append("candidateApplicationFormId", candidateApplicationFormId);
      if (candidateApplicationDocId) params.append("candidateApplicationDocId", candidateApplicationDocId);
      const { data } = await axios.get(
        `${API_URL.EXPIRY_NOTIFICATION.LOGS}?${params.toString()}`
      );
      return data.data;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  }
);

const candidateOperationSlice = createSlice({
  name: "candidateOperation",
  initialState: {
    candidate_form_list: null,
    candidate_document_list: null,
    candidate_form_document_list: null,
    form_history: null,
    candidate_messages: null,
    form_doc_list_loader: false,
  },
  reducers: {
    resetViewDetailVariable: (state) => {
      state.candidate_form_document_list = null;
      state.candidate_messages = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getCandidatesForms.fulfilled, (state, action) => {
        state.candidate_form_list = action.payload;
      })
      .addCase(getCandidatesDocuments.fulfilled, (state, action) => {
        state.candidate_document_list = action.payload;
      })
      .addCase(getFormsAndDocuments.pending, (state, action) => {
        state.candidate_form_document_list = null;
        state.form_doc_list_loader = true;
      })
      .addCase(getFormsAndDocuments.fulfilled, (state, action) => {
        state.candidate_form_document_list = action.payload;
        state.form_doc_list_loader = false;
      })
      .addCase(getFormsAndDocuments.rejected, (state, action) => {
        state.candidate_form_document_list = null;
        state.form_doc_list_loader = false;
      })
      .addCase(updateFormComplianceNotes.fulfilled, (state, action) => {
        const { formId, notes } = action.payload;
        const form = state.candidate_form_document_list?.forms?.find(
          (f) => f.id === formId
        );
        if (form) form.complianceNotes = notes;
      })
      .addCase(updateDocumentComplianceNotes.fulfilled, (state, action) => {
        const { docId, notes } = action.payload;
        const doc = state.candidate_form_document_list?.documents?.find(
          (d) => d.id === docId
        );
        if (doc) doc.complianceNotes = notes;
      })
      .addCase(getFormHistory.fulfilled, (state, action) => {
        state.form_history = action.payload;
      })
      .addCase(getCandidateMessages.fulfilled, (state, action) => {
        state.candidate_messages = action.payload;
      });
  },
});

export const { resetViewDetailVariable } = candidateOperationSlice.actions;
export default candidateOperationSlice.reducer;
